import { useEffect, useState, type RefObject } from 'react';

/*
 The squad cluster is laid out at an intrinsic size and scaled to fit its box so all five cards stay visible without scrolling. These constants split that size into the parts that scale with --player-card-scale and the gaps that don't. Keep them in step with the card rows in LobbyView.
 */
const SCALED_W = 330; // 3 cards x 110px
const FIXED_W = 32; // 2 x gap-4
const SCALED_H = 361; // fan offset 65 + card 148 + card 148
const FIXED_H = 16; // 2 x pt-2
const SAFETY = 4;

/**
 * Largest scale that fits the cluster inside 'ref's box on
 * both axis.
 * This can't be done in CSS: a scale() factor must be
 * unit-less, and CSS has no way to divide a length
 * by a length
 */
export function useFitScale(ref: RefObject<HTMLElement | null>, min = 0.45, max = 1.3): number {
  const [scale, setScale] = useState(min);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      const byWidth = (width - FIXED_W - SAFETY * 2) / SCALED_W;
      const byHeight = (height - FIXED_H - SAFETY * 2) / SCALED_H;
      setScale(Math.max(min, Math.min(max, byWidth, byHeight)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, min, max]);

  return scale;
}
